import { first } from 'lodash';
import { type QueryKey } from 'react-query';
import { type UseQueryOptions } from 'react-query/types/react/types';
import { getClosestEqualOrBiggerDivisibleNumber } from './number-utils';
import { Timestamp } from '../services/Timestamp';
import { apiClient } from '../services/ApiClient';
import { ApiEndpoints } from '../constants/api-constants';
import { Queries, QueryCacheConfig } from '../constants/query-constants';
import { Mode, type PlaybackViewModel } from '../types';

export const PlaybackFetchInterval: Timestamp = Timestamp.fromMinutes(60);
export const PlaybackPrefetchBufferSize: Timestamp = Timestamp.fromHours(3);

export interface TimePair {
  pastTimestamp: Timestamp;
  futureTimestamp: Timestamp;
}

export const getQueryOptions = (
  port: string,
  timestamp: Timestamp,
  mode: Mode,
  customOptions?: UseQueryOptions<PlaybackViewModel>
): UseQueryOptions<PlaybackViewModel> => {
  const [from, to] = timestamp?.isValid() ? getFetchRange(timestamp) : [0, 0];
  const { enabled, ...options } = customOptions ?? { enabled: true };
  return {
    queryKey: getPlaybackDataQueryKey(port, Timestamp.fromSeconds(from)),
    queryFn: getFetchPlaybackDataFunction(from, to, port),
    enabled: enabled && mode === Mode.Playback && timestamp?.isValid(),
    ...QueryCacheConfig[Queries.PlaybackData],
    ...options,
  };
};

export const getPlaybackDataQueryKey = (port: string, timestamp: Timestamp): string[] => [
  Queries.PlaybackData,
  port,
  timestamp.toISOString(),
];

export const getFetchPlaybackDataFunction = (
  from: number,
  to: number,
  port: string
): (() => Promise<PlaybackViewModel>) => {
  return async (): Promise<PlaybackViewModel> => {
    return apiClient.get(ApiEndpoints.getPlaybackData(port, from, to));
  };
};

export const getFetchRange = (timestamp: Timestamp): [number, number] => {
  const intervalInSeconds = PlaybackFetchInterval.toSeconds();
  const result = getClosestEqualOrBiggerDivisibleNumber(timestamp.toSeconds(), intervalInSeconds);

  return result > timestamp.toSeconds() ? [result - intervalInSeconds, result] : [result, result + intervalInSeconds];
};

export const getPrefetchTimePairs = (initialTimestamp: Timestamp): TimePair[] => {
  if (!initialTimestamp) {
    return [];
  }

  const currentTimestamp = Timestamp.fromSeconds(first(getFetchRange(initialTimestamp)));
  const minTimestamp = currentTimestamp.subtract(PlaybackPrefetchBufferSize);
  const maxTimestamp = currentTimestamp.add(PlaybackPrefetchBufferSize).add(PlaybackFetchInterval);
  let pastTimestamp = currentTimestamp;
  let futureTimestamp = currentTimestamp;

  const timePairs = [];

  while (
    pastTimestamp.valueInMilliseconds > minTimestamp.valueInMilliseconds &&
    futureTimestamp.valueInMilliseconds < maxTimestamp.valueInMilliseconds
  ) {
    pastTimestamp = pastTimestamp.subtract(PlaybackFetchInterval);
    futureTimestamp = futureTimestamp.add(PlaybackFetchInterval);

    timePairs.push({ pastTimestamp, futureTimestamp });
  }

  return timePairs;
};

export const isPlaybackQueryKey = (queryKey: QueryKey): boolean => queryKeyContains(queryKey, Queries.PlaybackData);

export const queryKeyContains = (queryKey: QueryKey, value: string): boolean => {
  if (typeof queryKey === 'string') {
    return false;
  }

  return (queryKey as unknown[]).some(part => part === value);
};

export const areQueryKeysEqual = (a: QueryKey, b: QueryKey): boolean => {
  if (typeof a !== typeof b) {
    return false;
  }

  if (a.length !== b.length) {
    return false;
  }

  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) {
      return false;
    }
  }

  return true;
};
